import React, { useEffect, useRef, useState } from "react";
import { useQueryClient } from "react-query";
import { generatePath, useHistory } from "react-router";
import cn from "classnames";
import { Image, Upload } from "lucide-react";
import ROUTES from "config/routing";
import { authorization } from "module/auth";
import { Card, CardTitle } from "campaign-builder/ui";
import useDesignManagement, { type Design } from "pages/creative/pageV2/useDesignManagement";
import { updateCampaignExtraData } from "graphQL/mutations/campaignExtraData";
import {
  resolveBuilderChannel,
  hasChannelConfig,
  getChannelConfig,
  type BuilderStepProps,
} from "campaign-builder/config";
import { useTargetingValidation, FieldError } from "campaign-builder/channels/flyering/targeting";
import { useCampaignData } from "campaign-builder/context/CampaignDataContext";
import { useClientConfig } from "campaign-builder/context/ClientConfigContext";
// Reused as-is from ADM — these have no channel/format coupling.
import { CreatePane } from "campaign-builder/channels/directmail/adm/design/CreatePane";
import { WandSparkleIcon } from "campaign-builder/channels/directmail/adm/design/icons";
import { useDesignSelection, type DesignView } from "./designSelection";
import { getFlyerFormatMeta } from "./formatMeta";
import { FormatSection } from "./FormatSection";
import { DesignGalleryGrid } from "./DesignGalleryGrid";
import { UploadPane } from "./UploadPane";
import { builderFormat } from "campaign-builder/formats/builderFormat";

type Source = "gallery" | "upload" | "create";

const SOURCES: { id: Source; label: string; icon: React.ReactNode }[] = [
  { id: "gallery", label: "Select from gallery", icon: <Image className="h-[18px] w-[18px]" /> },
  { id: "upload", label: "Upload design", icon: <Upload className="h-[18px] w-[18px]" /> },
  { id: "create", label: "Create new", icon: <WandSparkleIcon className="h-[18px] w-[18px]" /> },
];

const toView = (design: Design): DesignView => ({
  id: design.id,
  designName: design.designName,
  format: design.format,
  thumbnailUrl: design.thumbnailUrl,
  s3Link: design.s3Link,
  addressBox: design.addressBox,
});

/**
 * Shared Design step for the Flyering (H2H) and Door-to-Door (D2D) builder. Resolves
 * the channel from the campaign and drives the format section + gallery/upload/create
 * sources off its ChannelConfig. Reuses the (channel-generic) gallery/upload/create
 * panes; ADM keeps its own design step while mid-QA (dedup tracked in PAD-31539).
 *
 * Unlike ADM there is no address-box requirement and no tracking-invalidation modal
 * (Step 3 is static-only for these channels), so selecting a design is unconditional.
 */
export const DesignStep: React.FC<BuilderStepProps> = ({ campaign }) => {
  const channel = resolveBuilderChannel({ channel: campaign.channel, subtype: campaign.subtype });
  const config = channel && hasChannelConfig(channel) ? getChannelConfig(channel) : null;

  const { campaignData, clientId, campaignId, refetchCampaign } = useCampaignData();
  const { country } = useClientConfig();
  const queryClient = useQueryClient();
  const { selectedDesign, setSelectedDesign } = useDesignSelection();
  const { activateDesign, createDesign } = useDesignManagement({ clientId: clientId || "" });
  const history = useHistory();

  const countryCode = country?.code;
  const formats = config && countryCode ? config.getFormats(countryCode) : [];
  const singleFormat = formats.length === 1 ? formats[0].value : null;
  const defaultFormat = formats.find((format) => format.isDefault)?.value ?? formats[0]?.value;
  // Single-format channels are forced to their only option (ignoring any stale persisted
  // value, e.g. an old A6 on a US flyering campaign); multi-format markets use the saved
  // format or the default.
  const flyerType = singleFormat || campaignData?.flyerType || defaultFormat || "";
  const formatLabel = getFlyerFormatMeta(flyerType)?.label;
  const templateUrl = getFlyerFormatMeta(flyerType)?.template;

  const [source, setSource] = useState<Source>("gallery");
  const [selectError, setSelectError] = useState<string | null>(null);
  const [openingStudio, setOpeningStudio] = useState(false);

  const persistFormat = async (format: string, options?: { silent?: boolean }) => {
    if (!campaignId) return;
    try {
      await updateCampaignExtraData({ campaignId, flyerType: format });
      await refetchCampaign();
      // AC38 — the format drives the printing tier, so the server cost changes.
      // Refetch the shared channel-cost query so the Summary figure + order
      // quote recalculate for the new format. cancelRefetch: an invalidation
      // landing mid-fetch is otherwise deduped into the stale in-flight request,
      // whose pre-format price would then stick until the next edit.
      void queryClient.refetchQueries(["channelCampaignCost", campaignId], undefined, { cancelRefetch: true });
    } catch (err) {
      console.error("persistFormat error", err);
      if (!options?.silent) setSelectError("Failed to update the format. Please try again.");
    }
  };

  // Align the persisted format with the resolved one (single-format only option, or the
  // multi-format default) when they differ. Best-effort and silent — a failure here must
  // not surface a scary error on load; the user can still pick a design.
  const persistedFormat = useRef(false);
  useEffect(() => {
    if (persistedFormat.current) return;
    if (!campaignId || !flyerType || campaignData?.flyerType === flyerType) return;
    persistedFormat.current = true;
    void persistFormat(flyerType, { silent: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId, flyerType, campaignData?.flyerType]);

  const applyDesign = async (design: Design) => {
    setSelectedDesign(toView(design));
    setSelectError(null);
    if (!campaignId) return;
    try {
      // Persists the design→campaign link (activeRoomId). On failure the selection
      // wouldn't survive a reload, so surface it instead of failing silently.
      await activateDesign(campaignId, design.id);
    } catch (err) {
      console.error("activateDesign error", err);
      setSelectError("We couldn't save your design selection. Please try again.");
    }
  };

  // Selecting a design of a different (still channel-valid) format switches the
  // campaign format to match it.
  const selectDesign = (design: Design) => {
    if (design.format && design.format !== flyerType) void persistFormat(design.format);
    void applyDesign(design);
  };

  const handleGallerySelect = (design: Design) => {
    if (selectedDesign?.id === design.id) {
      setSelectedDesign(null);
      return;
    }
    selectDesign(design);
  };

  const handleFormatRowSelect = (format: string) => {
    // Changing the campaign format clears a selected design of a different format.
    if (selectedDesign?.format && selectedDesign.format !== format) setSelectedDesign(null);
    void persistFormat(format);
  };

  // The always-on CTA (AC4): advancing with no design selected flags the error.
  const { version: validationVersion } = useTargetingValidation();
  const lastValidation = useRef(0);
  useEffect(() => {
    if (validationVersion === lastValidation.current) return;
    lastValidation.current = validationVersion;
    if (!selectedDesign) setSelectError("Select a design to continue.");
  }, [validationVersion, selectedDesign]);

  const goToBuilder = (design: Pick<DesignView, "id" | "format" | "s3Link">) => {
    if (!clientId || !campaignId) return;
    const path = generatePath(ROUTES.FLYER_BUILDER, {
      clientId,
      campaignId,
      format: builderFormat(design.format, flyerType),
    });
    const params = new URLSearchParams();
    params.set("designId", design.id);
    if (design.s3Link) params.set("fileLink", design.s3Link);
    history.push(`${path}?${params.toString()}`);
  };

  const goCreateStudio = async () => {
    if (!clientId || !campaignId || !flyerType || openingStudio) return;
    setOpeningStudio(true);
    try {
      const user = authorization.getUser();
      const design = await createDesign({
        filename: "New design",
        s3Link: "",
        s3CmykLink: "",
        isUploaded: false,
        format: flyerType,
        authorId: user?.id != null ? String(user.id) : undefined,
      });
      if (!design?.id) throw new Error("createDesign returned no id");
      await activateDesign(campaignId, design.id);
      setSelectedDesign(toView(design));
      setSelectError(null);
      const path = generatePath(ROUTES.FLYER_BUILDER, { clientId, campaignId });
      history.push(`${path}?designId=${design.id}&newDesign=true`);
    } catch (error) {
      console.error("goCreateStudio error", error);
      setSelectError("We couldn't open the Creative Studio. Please try again.");
    } finally {
      setOpeningStudio(false);
    }
  };

  if (!config) return null;

  const allowedFormats = formats.map((format) => format.value);

  return (
    <Card className="flex flex-col gap-5 p-[25px]" data-targeting-section="design" data-cypress-id="design-step">
      <CardTitle>Design details</CardTitle>

      <FormatSection
        formats={formats}
        value={flyerType}
        channelName={config.design.formatDisclaimerChannel ?? config.label}
        countryCode={countryCode}
        onSelect={handleFormatRowSelect}
      />

      <div className="flex flex-col gap-2">
        <p className="text-foreground text-sm font-medium leading-5">Add your design</p>
        <div className="flex gap-2">
          {SOURCES.map((tab) => {
            const selected = source === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                data-cypress-id={`design-source-${tab.id}`}
                onClick={() => {
                  setSource(tab.id);
                  setSelectError(null);
                }}
                className={cn(
                  "flex flex-1 items-center gap-2.5 rounded-lg border-solid px-3 py-2.5 text-sm font-medium",
                  selected
                    ? "text-foreground border-[1.5px] border-[#222336] dark:border-slate-300"
                    : "text-muted-foreground border border-border"
                )}
              >
                {tab.icon}
                {tab.label}
              </button>
            );
          })}
        </div>
        <FieldError message={selectError} />
      </div>

      {source === "gallery" && clientId && (
        <DesignGalleryGrid
          clientId={clientId}
          format={flyerType}
          allowedFormats={allowedFormats}
          activeDesignId={selectedDesign?.id ?? null}
          templateUrl={templateUrl}
          templateLabel={formatLabel}
          onSelect={handleGallerySelect}
          onEdit={(design) => goToBuilder(toView(design))}
        />
      )}

      {source === "upload" && (
        <UploadPane
          designConfig={config.design}
          format={flyerType}
          formatLabel={formatLabel}
          countryCode={countryCode}
          campaignId={campaignId ?? undefined}
          clientId={clientId}
          templateUrl={templateUrl}
          templateLabel={formatLabel}
          onCommitted={(design) => {
            setSource("gallery");
            selectDesign(design);
          }}
        />
      )}

      {source === "create" && (
        <CreatePane onOpenCreativeStudio={() => void goCreateStudio()} isOpeningCreativeStudio={openingStudio} />
      )}
    </Card>
  );
};
